import { Grid, Typography, Checkbox, FormControl } from "@mui/material";
import Box from "@mui/material/Box";
import { useContext, useEffect, useState, VFC } from "react";
import ButtonArea from "../common/ButtonArea";
import Button from "@mui/material/Button";
import { BackButton } from "../common/BackButton";
import { useLocation, useNavigate } from "react-router-dom";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import { TerminalImportConfirmResponse, TerminalImportForm, TerminalImportFormSchema, TerminalImportRequest } from "./api/TerminalForm";
import { ResultDialog, ResultResponse } from "../common/dialog/ResultDialog";
import { SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { TerminalService } from "./api/TerminalService";
import { AxiosResponse } from "axios";
import Constants from "../../utils/Constants";
import { UserContext } from "../../UserProvider";
import { Loading } from "../common/Loading";

const TerminalImportConfirmation: VFC = () => {
  const PAGE_NAME = "端末インポート確認画面";

  /***************************************************************
   * State管理
   **************************************************************/
  const navigate = useNavigate();
  // 前画面パラメータ
  const location = useLocation();
  const [importCheckResponse] = useState<TerminalImportConfirmResponse>(
    location.state.importCheckResponse
  );

  // リクエスト
  const [request, setRequest] = useState(new TerminalImportRequest());

  // 実行ボタン(活性・非活性)
  const [isDisable, setIsDisable] = useState(true);

  // 共通ダイアログ
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // 処理結果
  const [resultResponse, setResultResponse] = useState<ResultResponse>({
    status: "",
    code: "",
    displayMessage: "",
    result: [],
    errorMessages: [],
  });

  // ローディング
  const [isLoading, setIsLoading] = useState(false);

  const cache = useContext(UserContext);

  /***************************************************************
   * 共通処理
   **************************************************************/
  // 処理結果ダイアログ(CLOSE)
  const handleResultDialogClose = () => {
    setIsDialogOpen(false);
    navigate(-1);
  };
  // 処理結果を設定
  const setStateResultResponse = (
    status: string,
    code: string,
    displayMessage: string,
    result: string
  ) => {
    resultResponse.status = status;
    resultResponse.code = code;
    resultResponse.displayMessage = displayMessage;
    resultResponse.result = [result];

    setIsDialogOpen(true);
    setResultResponse({
      status,
      code,
      displayMessage,
      result: [result],
      errorMessages: [],
    });
  };

  /***************************************************************
   * 初期表示処理
   **************************************************************/
  useEffect(() => {
    // インポートチェック時刻
    setRequest({
      importCheckDate: importCheckResponse.importCheckDate,
      tenantName: importCheckResponse.tenantName,
      tenantId: importCheckResponse.tenantId,
    });
  }, []);

  /***************************************************************
   * インポート処理
   **************************************************************/
  // 実行ボタン押下(入力チェック)
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<TerminalImportForm>({
    resolver: yupResolver(TerminalImportFormSchema),
    reValidateMode: "onSubmit",
  });
  // インポートAPI
  const handleExecuteBtn: SubmitHandler<TerminalImportForm> = (hookForm) => {
    setIsLoading(true);
    const service = new TerminalService();
    service
      .import(request)
      .then((response: AxiosResponse) => {
        if (Constants.ERR_STATUS_OK === response.data.status) {
          // OK
          setIsDialogOpen(true);
          setResultResponse(response.data);
          // 一覧画面に戻った時、前回の検索条件・ソート順・ページ番号を継承してリストを再読込みする。
          cache.isFlushView = true;
        } else {
          // NG
          setIsDialogOpen(true);
          setIsDisable(true);
          setResultResponse(response.data);
        }
      })
      .catch((err) => {
        setStateResultResponse(
          Constants.ERR_STATUS_NG,
          err.response.status,
          err.response.statusText,
          Constants.E_MSG_SYSTEM_ERR
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };
  // チェックボックス処理
  const handleCheck = (e: React.ChangeEvent<HTMLInputElement>) => {
    // エラーフラグがtrueの場合、非活性状態とする
    // インポートチェックボックスにチェックが入っていない場合、非活性状態とする
    setIsDisable(importCheckResponse.isError || !e.target.checked);
  };

  return (
    <>
      <Box
        display={"flex"}
        flexDirection={"column"}
        style={{ height: "calc(100vh - 64px)", width: "calc(100vw - 200px)" }}
      >
        {/* ボタンの配置 */}
        <ButtonArea pageName={PAGE_NAME} isBackBtn={true} isEdit={true}>
          <Box mr={2}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit(handleExecuteBtn)}
              disabled={isDisable}
            >
              実行
            </Button>
          </Box>
        </ButtonArea>
        <Box sx={{ p: 4 }} flex={"1"} style={{ overflowY: "scroll" }}>
          <Box sx={{ py: 2, px: 8 }}>
            {/* インポート内容にエラーがない場合、インポート確認メッセージを表示する */}
            {!importCheckResponse.isError && (
              <FormControl error={errors.check ? true : false}>
                <FormGroup>
                  <FormControlLabel
                    control={<Checkbox {...register("check")} onChange={handleCheck} />}
                    label={`${importCheckResponse.importFileName}（データ件数:${importCheckResponse.dataCnt}件）の内容をテナント:${importCheckResponse.tenantName} にインポートします。`}
                  />
                </FormGroup>
              </FormControl>
            )}

            {/* インポート内容にエラーがある場合、エラー行を表示する */}
            {importCheckResponse.isError && (
              <>
                {/* エラーメッセージの表示 */}
                <Typography variant="h6" align="center" sx={{ pb: 2 }}>
                  CSVファイルの以下の行でエラーが発生したため、インポートできません。
                </Typography>
                <Grid
                  container
                  spacing={2}
                  alignItems="center"
                  flexDirection={"row"}
                  justifyContent={"center"}
                >
                  {/* エラー行の表示 */}
                  {importCheckResponse.errorList.map((row, index) => (
                    <Grid
                      item
                      key={index}
                      style={{ textAlign: "center" }}
                    >
                      <Typography variant="h6">{row}行目：</Typography>
                    </Grid>
                  ))}
                  {importCheckResponse.errorMessages && (
                    <Box>
                      {importCheckResponse.errorMessages.map((message, index) => (
                        <Typography key={index} variant="h6" align="center" sx={{ mt: 2 }}>
                          {message}
                        </Typography>
                        
                      ))}
                    </Box>
                  )}
                </Grid>

                {/* エラー件数が10件を超えた場合、エラーメッセージを表示する */}
                {10 <= importCheckResponse.errorList.length && (
                  <Typography variant="h6" align="center" sx={{ mt: 2 }}>
                    エラーが10件を超えたため、エラー検知を終了します。
                  </Typography>
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>

      {/* 処理結果ダイアログ */}
      <ResultDialog
        isDialogOpen={isDialogOpen}
        resultResponse={resultResponse}
        handler={handleResultDialogClose}
      />
      {/* 処理ローディング */}
      <Loading isLoading={isLoading} />
    </>
  );
};

export default TerminalImportConfirmation;
